/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useHotelPageSettingsService } from "../hooks/useAdminServices";
import { useHotel } from "./HotelContext";
import { logger } from "../utils/logger";

interface PageSettingsContextType {
  disabledPages: string[];
  refreshPageSettings: () => Promise<void>;
  isPageDisabled: (pageIdentifier: string) => boolean;
}

const PageSettingsContext = createContext<PageSettingsContextType | undefined>(
  undefined,
);

export const PageSettingsProvider: React.FC<{ children: React.ReactNode; initialDisabledPages?: string[] }> = ({
  children,
  initialDisabledPages,
}) => {
  const { isAuthenticated } = useAuth0();
  const { myHotelInfo } = useHotel();
  const [disabledPages, setDisabledPages] = useState<string[]>(initialDisabledPages || []);
  const [hasInitialized, setHasInitialized] = useState(!!initialDisabledPages);
  const pageSettingsService = useHotelPageSettingsService();
  const isUpdatingRef = useRef(false);

  // Check if user has a hotel before making API calls
  const hasHotel = !!myHotelInfo?.hotelId;

  const fetchDisabledPages = useCallback(
    async (updateLocalStorage = true) => {
      // Only fetch if user has a hotel - avoids 403 errors
      if (!isAuthenticated || !pageSettingsService || !hasHotel || isUpdatingRef.current) {
        return;
      }

      isUpdatingRef.current = true;
      try {
        const result = await pageSettingsService.getDisabledPages();
        if (result.success) {
          setDisabledPages(result.data || []);
          // Only update localStorage if requested (not when responding to storage events)
          if (updateLocalStorage) {
            localStorage.setItem(
              "pageSettingsLastUpdated",
              Date.now().toString(),
            );
          }
        }
      } catch (error) {
        logger.error("Failed to fetch disabled pages:", error);
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [isAuthenticated, pageSettingsService, hasHotel],
  );

  const refreshPageSettings = useCallback(async () => {
    return fetchDisabledPages(true);
  }, [fetchDisabledPages]);

  const isPageDisabled = useCallback(
    (pageIdentifier: string) => {
      return disabledPages.includes(pageIdentifier);
    },
    [disabledPages],
  );

  // Initialize once when service becomes available AND user has a hotel
  useEffect(() => {
    if (isAuthenticated && pageSettingsService && hasHotel && !hasInitialized) {
      setHasInitialized(true);
      fetchDisabledPages(true);
    }
  }, [isAuthenticated, pageSettingsService, hasHotel, hasInitialized, fetchDisabledPages]);

  // Listen for storage events to sync across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only respond to changes from other tabs/windows
      if (
        e.key === "pageSettingsLastUpdated" &&
        e.storageArea !== window.localStorage
      ) {
        fetchDisabledPages(false); // Don't update localStorage when syncing from another tab
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchDisabledPages]);

  return (
    <PageSettingsContext.Provider
      value={{ disabledPages, refreshPageSettings, isPageDisabled }}
    >
      {children}
    </PageSettingsContext.Provider>
  );
};

export const usePageSettings = () => {
  const context = useContext(PageSettingsContext);
  if (!context) {
    throw new Error("usePageSettings must be used within PageSettingsProvider");
  }
  return context;
};
